import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { CTABand } from "@/components/shared/CTABand";
import { getIndustry, INDUSTRIES } from "@/data/industries";
import { useSEO } from "@/lib/seo";

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = getIndustry(slug);

  useSEO({
    title: industry ? `${industry.name} Industry Rolls` : "Industry",
    description: industry ? `${industry.short} Manufactured and refurbished by Vikas Rolls since 1970.` : "",
    jsonLd: industry && {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `Industrial rolls for the ${industry.name} industry`,
      provider: { "@type": "Organization", name: "Vikas Rolls Private Limited" },
      description: industry.intro,
    },
  });

  if (!industry) return <Navigate to="/404" replace />;

  const idx = INDUSTRIES.findIndex((i) => i.slug === slug);
  const prev = INDUSTRIES[(idx - 1 + INDUSTRIES.length) % INDUSTRIES.length];
  const next = INDUSTRIES[(idx + 1) % INDUSTRIES.length];

  return (
    <>
      <PageHero
        chapter={industry.index}
        label="Industry"
        title={industry.name}
        sub={industry.intro}
        crumbs={[{ label: "Industries", to: "/industries" }, { label: industry.name }]}
      />

      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-16">
            {industry.images.map((img, i) => (
              <div key={img} className="corner-marks border border-line overflow-hidden">
                <img src={img} alt={`${industry.name} industry application ${i + 1}`} loading="lazy" className="img-industrial w-full h-full object-cover" />
              </div>
            ))}
          </Reveal>

          <div className="mb-6 flex items-baseline gap-4">
            <span className="font-mono-tech text-xs tracking-[0.25em] text-amber-brand uppercase">Rolls We Supply</span>
            <span className="h-px flex-1 bg-line" aria-hidden="true" />
          </div>

          <Stagger className="grid md:grid-cols-2 gap-8">
            {industry.groups.map((group) => (
              <StaggerItem key={group.title}>
                <div className="border border-line bg-ink-2 h-full">
                  <h2 className="font-display font-bold text-lg text-steel-light px-6 py-4 border-b border-line flex items-center justify-between">
                    {group.title}
                    <span className="font-mono-tech text-[10px] text-steel/60">{group.rolls.length}</span>
                  </h2>
                  <ul className="p-6 grid gap-2.5">
                    {group.rolls.map((roll) => (
                      <li key={roll} className="flex items-start gap-3 text-sm md:text-base text-steel">
                        <span className="w-1.5 h-1.5 bg-amber-brand rotate-45 flex-none mt-2" aria-hidden="true" />
                        {roll}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          {industry.note && (
            <Reveal className="mt-10">
              <p className="border border-amber-brand/40 bg-amber-brand/5 text-steel text-sm px-6 py-4 max-w-2xl">
                {industry.note}
              </p>
            </Reveal>
          )}

          <div className="mt-16 flex items-center justify-between border-t border-line pt-8">
            <Link to={`/industries/${prev.slug}`} data-testid="industry-prev-link" className="group inline-flex items-center gap-3 text-steel hover:text-amber-brand">
              <ArrowLeft size={17} className="transition-transform duration-300 group-hover:-translate-x-1" />
              <span className="hidden sm:block font-display font-bold">{prev.name}</span>
              <span className="sm:hidden font-mono-tech text-xs uppercase">Prev</span>
            </Link>
            <Link to="/industries" data-testid="industry-all-link" className="font-mono-tech text-xs uppercase tracking-[0.2em] text-amber-brand hover:text-steel-light">
              All industries
            </Link>
            <Link to={`/industries/${next.slug}`} data-testid="industry-next-link" className="group inline-flex items-center gap-3 text-steel hover:text-amber-brand">
              <span className="hidden sm:block font-display font-bold">{next.name}</span>
              <span className="sm:hidden font-mono-tech text-xs uppercase">Next</span>
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
      <CTABand title={`Specifying rolls for a ${industry.name.toLowerCase()} application?`} />
    </>
  );
}
